/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { inject, observer } from 'mobx-react';
import { computed, decorate, observable, runInAction } from 'mobx';

import DropDown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import Toggle from '@amzn/base-ui/dist/parts/helpers/fields/Toggle';
import { getDeployedRegions } from '../../../../helpers/api';
import { getRegionDisplayText } from '../../../../helpers/aws-regions';
import { enableWorkspaceScheduler } from '../../../../helpers/settings';
import BaseEnvTypeConfigStep from './BaseEnvTypeConfigStep';

class AccessControlStep extends BaseEnvTypeConfigStep {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.regionOptions = [];
    });
  }

  componentDidMount() {
    this.loadDeployedRegions();
  }

  async loadDeployedRegions() {
    try {
      const result = await getDeployedRegions();
      const { primaryRegion, additionalRegions = [] } = result;
      const allRegions = [primaryRegion, ...additionalRegions];
      runInAction(() => {
        this.regionOptions = allRegions.map((r) => ({ key: r, value: r, text: getRegionDisplayText(r) }));
      });
    } catch (e) {
      // If the call fails, leave options empty — the field is optional
      runInAction(() => {
        this.regionOptions = [];
      });
    }
  }

  renderFormFields({ form, processing }) {
    const allowRoleIdsField = form.$('allowRoleIds') || [];
    const denyRoleIdsField = form.$('denyRoleIds') || [];
    const allowedRegionsField = form.$('allowedRegions') || [];

    const userRoleOptions = _.filter(
      this.userRolesStore.dropdownOptions,
      // Also currently not supporting external roles i.e., guest and external-researcher so exclude them from
      // "allow" or "deny" list
      (r) => !_.includes(['external-researcher', 'guest'], _.toLower(r.value)),
    );
    return (
      <>
        <DropDown
          dataTestId="allow-dropdown"
          field={allowRoleIdsField}
          options={userRoleOptions}
          selection
          multiple
          fluid
          disabled={processing}
        />
        <DropDown field={denyRoleIdsField} options={userRoleOptions} selection multiple fluid disabled={processing} />
        <DropDown
          dataTestId="allowed-regions-dropdown"
          field={allowedRegionsField}
          options={this.regionOptions}
          selection
          multiple
          fluid
          disabled={processing}
        />
        {/* The idle-stop default and its lock are administrative constraints on a configuration,
            which is what this step already carries, so they belong here rather than with the basic
            information. Both stay out of the form entirely when the deployment has not enabled the
            scheduler (Requirement 1.8). */}
        {enableWorkspaceScheduler && this.renderIdleStopFields({ form, processing })}
      </>
    );
  }

  renderIdleStopFields({ form, processing }) {
    const idleStopMinutesField = form.$('idleStopMinutes');
    const idleStopLockedField = form.$('idleStopLocked');

    return (
      <>
        <Input dataTestId="idle-stop-minutes-input" field={idleStopMinutesField} type="number" disabled={processing} />
        {/* The lock is wired through the shared Toggle field rather than a hand-wired control: every
            other boolean form field in the repository uses Toggle or YesNo, which carry the label,
            the explanatory text, the sync and the error display. A raw checkbox with its own onChange
            would be a second way of doing the same job in the same form. */}
        <Toggle field={idleStopLockedField} disabled={processing} />
      </>
    );
  }

  get userRolesStore() {
    return this.props.userRolesStore;
  }
}

decorate(AccessControlStep, {
  userRolesStore: computed,
  regionOptions: observable,
});
export default inject('userRolesStore')(observer(AccessControlStep));
